/**
 * Hook for fetching pending decision approvals for consultants
 * 
 * Fetches decisions that require consultant approval (severity=block)
 * and are awaiting approval.
 * 
 * @version 1.0.0
 * @date 2026-01-29
 */

import { useState, useEffect, useCallback } from 'react';
import { projectId } from '../utils/supabase/info';
import { getAuthHeaders } from '../utils/apiHeaders';

interface PendingDecision {
  historyId: string;
  companyId: string;
  companyName: string;
  engagementId?: string;
  engagementName?: string;
  decisionNodeId: string;
  decisionTitle: string;
  severity: 'warning' | 'block';
  selectedOption: string;
  overrideReason?: string;
  evaluatedAt: string;
  evaluatedBy: string;
  requiresApproval: boolean;
  approvedBy?: string;
  approvedAt?: string;
}

interface UsePendingDecisionsResult {
  pendingDecisions: PendingDecision[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  pendingCount: number;
}

export function usePendingDecisions(
  consultantId: string,
  accessToken: string,
  enabled: boolean = true
): UsePendingDecisionsResult {
  const [pendingDecisions, setPendingDecisions] = useState<PendingDecision[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPendingDecisions = useCallback(async () => {
    if (!enabled || !consultantId || !accessToken) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/decision-history/consultant/${consultantId}/pending`,
        {
          headers: getAuthHeaders(accessToken),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch pending decisions: ${response.statusText}`);
      }

      const data = await response.json();
      setPendingDecisions(data.pendingDecisions || []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      console.error('Error fetching pending decisions:', errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [consultantId, accessToken, enabled]);

  useEffect(() => {
    fetchPendingDecisions();
  }, [fetchPendingDecisions]);

  return {
    pendingDecisions,
    loading,
    error,
    refetch: fetchPendingDecisions,
    pendingCount: pendingDecisions.length,
  };
}